import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/siteInfo';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <SEOHead title="文章不存在" description="您访问的文章不存在或已被移除" />
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
              <i className="ri-file-unknow-line text-2xl text-accent-600"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 font-heading mb-2">文章不存在</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line text-xs"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.slug}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: category?.name || article.category, item: `${siteInfo.url}/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `${siteInfo.url}/article/${article.slug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.slug}`}
        ogImage={article.image}
        ogType="article"
        schema={[articleSchema, breadcrumbSchema]}
      />
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article>
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category?.name || article.category, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="relative h-[400px] md:h-[480px] rounded-2xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className="flex items-center gap-3 mb-3">
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium cursor-pointer"
                    >
                      {category.name}
                    </Link>
                  )}
                  <span className="text-xs text-background-200">{article.date}</span>
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 font-heading leading-tight">
                  {article.title}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-6 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200/60">
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-line text-xs"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line text-xs"></i>
                </div>
                阅读约 {article.readTime} 分钟
              </span>
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-eye-line text-xs"></i>
                </div>
                {article.views.toLocaleString()} 次阅读
              </span>
            </div>

            <div
              className="prose prose-lg max-w-none mb-12 text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mb-12 pt-6 border-t border-background-200/60">
              <span className="text-sm font-medium text-foreground-700 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>

            {relatedArticles.length > 0 && (
              <div className="border-t border-background-200/60 pt-12 pb-16">
                <h3 className="text-xl font-bold text-foreground-950 font-heading mb-6">相关推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/50 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-foreground-900 font-heading line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-2">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}